/**
 * Der Bestaetigungsdialog beim Ueberschreiben eines vorhandenen Dokuments - Portierung des
 * statischen Dialogs `#documentOverwriteOverlay` (`index.html:3886-3907`) samt `uploadDocFor`
 * (`:12685-12706`, der Teil, der den Dialog OEFFNET) und `confirmDocumentOverwrite`
 * (`:12714-12726`, die Pruefung "Begruendung >= 3 Zeichen").
 *
 * ================= WARUM `createPortal` nach `document.body` =================
 *
 * `#documentOverwriteOverlay` ist im Bestand ein STATISCHES Element der App-Huelle - ein Geschwister
 * von `#drawer`/`#overlay`, ausserhalb von `drawerDocsTab`s zurueckgegebenem Markup, immer im DOM
 * (nur per `.open`-Klasse umgeschaltet). Diese Huelle gibt es in der SPA noch nicht (`App.tsx` ist
 * fuer diesen Task tabu) - anders als bei Task 18s `AufgabenDetail` (das nach dem BEREITS von
 * `Drawer.tsx` erzeugten `#drawer` portalt) gibt es hier keinen vorhandenen Anker. Diese Komponente
 * portalt deshalb direkt nach `document.body`, das in jsdom UND im Browser immer existiert - der
 * naheliegendste Platzhalter, bis eine App-Huelle einen eigenen Modal-Wurzelknoten anbietet.
 * Einhaengepunkt fuer den Merge, siehe Task-Bericht.
 *
 * ================= Eine notwendige Abweichung in der REIHENFOLGE, nicht im ERGEBNIS =============
 *
 * Im Bestand oeffnet der Dialog OHNE gewaehlte Datei; erst der Bestaetigen-Knopf
 * ("Datei zum Überschreiben wählen") oeffnet danach den nativen Dateiauswahldialog
 * (`startDocumentUpload`, `:12673`). In dieser Portierung waehlt die Nutzerin die Datei bereits ueber
 * das beschriftete Datei-Feld der Checkliste (`Checkliste.tsx`) - der Dialog erscheint DANACH, mit der
 * bereits gewaehlten Datei im Gepaeck, und best­aetigt nur noch die Begruendung. Der Kern der Pflicht
 * ("keine Begruendung -> keine Ueberschreibung", `index.html:12718-12722`) ist identisch; nur WANN im
 * Ablauf die Datei gewaehlt wird, unterscheidet sich - eine Notwendigkeit des Datei-Feld-Zugriffs
 * (siehe Kopfkommentar `Checkliste.tsx`), keine fachliche Aenderung. Der Knopftext heisst hier deshalb
 * "Überschreiben" statt "Datei zum Überschreiben wählen" - es gibt nichts mehr zu waehlen.
 */
import { createPortal } from "react-dom";
import { useState } from "react";
import type { OrderDocument } from "../../../lib/types.ts";

/**
 * `icon(name, className)` (`index.html:4016`) samt der Nachbearbeitung durch `hydrateIcons`
 * (`:4021`) - die gemeinsame Registratur zeichnet das fertige `<svg class="lucide lucide-…">`.
 *
 * Bis `feat/09-spa-ikonen` stand hier eine LOKALE Funktion, die nur den Platzhalter
 * `<i data-lucide="…">` erzeugte. Den hydriert in der SPA niemand (die UMD-Fassung laeuft allein
 * im Baseline-Rundlauf des Playwright-Harness) - es blieb eine leere Flaeche. Siehe `Ikone.tsx`.
 */
import { Ikone } from "../../Ikone.tsx";

export interface UeberschreibDialogProps {
  /** Beschriftung des Dokumenttyps (`docType.label`, `index.html:12695`). */
  typLabel: string;
  /** Die vorhandene(n) Datei(en) dieses Typs (`existing`, `index.html:12692-12697`). */
  bestehende: readonly OrderDocument[];
  /** `closeDocumentOverwriteDialog()` (`index.html:12708-12712`). */
  onAbbrechen: () => void;
  /** `confirmDocumentOverwrite()` (`index.html:12714-12726`) - liefert die geprüfte Notiz. */
  onBestaetigen: (notiz: string) => void;
}

export function UeberschreibDialog({
  typLabel,
  bestehende,
  onAbbrechen,
  onBestaetigen,
}: UeberschreibDialogProps) {
  const [notiz, setNotiz] = useState("");
  // `index.html:12718`: `if (note.length < 3)`.
  const bereit = notiz.trim().length >= 3;

  return createPortal(
    <div
      className="board-editor-overlay open"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onAbbrechen();
      }}
    >
      <section
        className="board-editor-dialog overwrite-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="documentOverwriteTitle"
      >
        <div className="board-editor-head">
          <div className="board-editor-head-copy">
            <h2 id="documentOverwriteTitle">Dokument überschreiben</h2>
            <p>
              {typLabel} ist bereits hinterlegt. Die Begründung wird dauerhaft protokolliert.
            </p>
          </div>
          <button className="icon-btn" type="button" onClick={onAbbrechen} title="Abbrechen">
            <Ikone name="x" />
          </button>
        </div>
        <div className="board-editor-body overwrite-body">
          {/*
            Nachbesserung (Pruefung b33c2006, Punkt 3, 07.09.2026): `bestehende` kann seit dem
            Nebenbefund "Duplikat-Gate uebersieht Verknuepfungen ohne Datei" (DokumenteTab.tsx#
            weiterOhneTreffer) leer sein - eine document_links-Zeile OHNE Datei (Status
            offen/entfaellt) loest diesen Dialog jetzt ebenfalls aus. Eine "Aktuell hinterlegt"-
            Ueberschrift ohne einen einzigen Eintrag darunter widersprach genau diesem Fall (der Typ
            ist belegt, aber es liegt gar keine Datei vor) - die ganze Sektion faellt jetzt weg, statt
            leer zu bleiben.
          */}
          {bestehende.length ? (
            <div className="overwrite-current">
              <strong>Aktuell hinterlegt</strong>
              {bestehende.map((d, i) => (
                <span key={i}>{d.name as string}</span>
              ))}
            </div>
          ) : null}
          <label className="overwrite-note">
            Begründung für das Überschreiben <span>Pflichtfeld</span>
            <textarea
              minLength={3}
              maxLength={600}
              placeholder="Warum wird das vorhandene Dokument ersetzt?"
              value={notiz}
              onChange={(e) => setNotiz(e.currentTarget.value)}
            />
          </label>
        </div>
        <div className="board-editor-foot">
          <button className="btn-sec" type="button" onClick={onAbbrechen}>
            <Ikone name="x" />
            Abbrechen
          </button>
          <button
            className="btn-primary"
            type="button"
            disabled={!bereit}
            onClick={() => onBestaetigen(notiz.trim())}
          >
            <Ikone name="refresh-cw" />
            Überschreiben
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
}
